import { supabase } from './supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

export type AccountRow = {
  id: string
  code: string
  name: string
  account_type: string
  subgroup_id: string | null
  subgroup_code: string | null
  subgroup_name?: string
}

export type AccountWithBalance = {
  account: AccountRow
  balance: number
}

export interface BSLine {
  label: string
  amount: number
  comparative?: number
  noteRef?: number
  isSubtotal?: boolean
  isTotal?: boolean
  isNegative?: boolean
  subgroupCode: string
  accounts: AccountWithBalance[]
}

export type StatementLine = {
  id: string
  code: string
  name: string
  balance: number
  comparative?: number
  noteRef?: number
  subgroup_code?: string | null
  subgroup_name?: string | null
}

export type StatementSection = {
  title: string
  lines: StatementLine[]
  total: number
  comparativeTotal?: number
}

export type BalanceSheetData = {
  asOfDate: string
  comparativeDate?: string
  nonCurrentAssets: BSLine[]
  currentAssets: BSLine[]
  equity: BSLine[]
  nonCurrentLiabilities: BSLine[]
  currentLiabilities: BSLine[]
  totalNonCurrentAssets: number
  totalCurrentAssets: number
  totalAssets: number
  totalNonCurrentLiabilities: number
  totalCurrentLiabilities: number
  totalLiabilities: number
  totalEquity: number
  comparativeTotalNonCurrentAssets?: number
  comparativeTotalCurrentAssets?: number
  comparativeTotalAssets?: number
  comparativeTotalNonCurrentLiabilities?: number
  comparativeTotalCurrentLiabilities?: number
  comparativeTotalLiabilities?: number
  comparativeTotalEquity?: number
  isBalanced: boolean
}

export type IncomeStatementData = {
  fromDate: string
  toDate: string
  comparativeFromDate?: string
  comparativeToDate?: string
  revenue: StatementSection
  otherIncome: StatementSection
  costOfSales: StatementSection
  grossProfit: number
  comparativeGrossProfit?: number
  operatingExpenses: StatementSection
  operatingProfit: number
  comparativeOperatingProfit?: number
  financeIncome: StatementSection
  financeCosts: StatementSection
  profitBeforeTax: number
  comparativeProfitBeforeTax?: number
  incomeTax: number
  comparativeIncomeTax?: number
  profitAfterTax: number
  comparativeProfitAfterTax?: number
}

export type CashFlowData = {
  fromDate: string
  toDate: string
  netProfit: number
  depreciation: number
  amortisation: number
  changeReceivables: number
  changeInventory: number
  changePayables: number
  changePrepayments: number
  changeAccruedLiabilities: number
  netOperating: number
  purchaseFixedAssets: number
  proceedsAssetDisposal: number
  netInvesting: number
  loanMovement: number
  ownerDrawings: number
  dividendsPaid: number
  netFinancing: number
  netCashMovement: number
  openingCash: number
  closingCash: number
}

export type EquityComponent = {
  key: string
  name: string
  opening: number
  movement: number
  closing: number
  isTotal: boolean
}

export type ChangesInEquityData = {
  fromDate: string
  toDate: string
  components: EquityComponent[]
}

// ─── Subgroup Display Order ───────────────────────────────────────────────────

// Display order for Current Assets
const CURRENT_ASSET_ORDER: Record<string, number> = {
  INV: 1, AR: 2, PP: 3, CB: 4, OCA: 5,
}

// Display order for Non-Current Assets
const NON_CURRENT_ASSET_ORDER: Record<string, number> = {
  FA: 1, ACD: 2, LTI: 3, IG: 4,
}

// Display order for Current Liabilities
const CURRENT_LIAB_ORDER: Record<string, number> = {
  AP: 1, TL: 2, STL: 3, OCL: 4,
}

// ─── Subgroup label mapping ───────────────────────────────────────────────────

const SUBGROUP_LABELS: Record<string, { label: string; noteRef?: number }> = {
  INV: { label: 'Inventories', noteRef: 5 },
  CB:  { label: 'Cash and Bank Balances', noteRef: 7 },
  AR:  { label: 'Trade and Other Receivables', noteRef: 6 },
  PP:  { label: 'Prepayments' },
  OCA: { label: 'Other Current Assets' },
  FA:  { label: 'Property, Plant & Equipment', noteRef: 4 },
  LTI: { label: 'Long-Term Investments' },
  IG:  { label: 'Intangible Assets' },
  AP:  { label: 'Trade and Other Payables', noteRef: 10 },
  TL:  { label: 'Tax Liabilities' },
  OCL: { label: 'Other Current Liabilities' },
  STL: { label: 'Short-Term Borrowings' },
  LTL: { label: 'Borrowings', noteRef: 9 },
  OWF: { label: 'Share Capital' },
  RE:  { label: 'Retained Earnings' },
}

// ─── Balance Sheet ────────────────────────────────────────────────────────────

type BalanceSheetRpcRow = {
  section: string
  section_order: number
  account_id: string | null
  code: string | null
  name: string | null
  group_code: string | null
  group_name: string | null
  subgroup_code: string | null
  subgroup_name: string | null
  amount: number | string | null
  is_subtotal: boolean
}

export async function calculateBalanceSheet(
  tenantId: string,
  asOfDate: string,
  locationId?: string | null,
  comparativeAsOfDate?: string,
): Promise<BalanceSheetData> {
  async function fetchRpc(asOf: string): Promise<BalanceSheetRpcRow[]> {
    const { data, error } = await supabase.rpc('get_balance_sheet', {
      p_tenant_id: tenantId,
      p_location_id: locationId ?? undefined,
      p_as_of_date: asOf,
      p_include_zero: false,
    })
    if (error) throw new Error(error.message)
    return (data ?? []) as BalanceSheetRpcRow[]
  }

  const wantComp = !!comparativeAsOfDate
  const [rows, compRows] = await Promise.all([
    fetchRpc(asOfDate),
    wantComp
      ? fetchRpc(comparativeAsOfDate as string)
      : Promise.resolve<BalanceSheetRpcRow[]>([]),
  ])

  const num = (v: number | string | null | undefined): number => Number(v ?? 0)

  const compByAccount = new Map<string, number>()
  const compSectionTotal = new Map<string, number>()
  const compSynthetic = new Map<string, number>()
  for (const r of compRows) {
    const amt = num(r.amount)
    if (r.is_subtotal) {
      compSynthetic.set(r.section, amt)
    } else {
      if (r.account_id) compByAccount.set(r.account_id, (compByAccount.get(r.account_id) ?? 0) + amt)
      compSectionTotal.set(r.section, (compSectionTotal.get(r.section) ?? 0) + amt)
    }
  }

  function accountTypeForSection(section: string): string {
    if (section === 'non_current_assets' || section === 'current_assets') return 'asset'
    if (section === 'current_liabilities' || section === 'non_current_liabilities') return 'liability'
    if (section === 'equity') return 'equity'
    return ''
  }

  type RowWithComp = { row: BalanceSheetRpcRow; balance: number; comp: number }

  const bySection: Record<string, RowWithComp[]> = {
    non_current_assets: [],
    current_assets: [],
    current_liabilities: [],
    non_current_liabilities: [],
    equity: [],
  }
  const synthetic = new Map<string, number>()

  for (const r of rows) {
    if (r.is_subtotal) {
      synthetic.set(r.section, num(r.amount))
    } else if (bySection[r.section]) {
      const balance = num(r.amount)
      const comp = wantComp && r.account_id ? compByAccount.get(r.account_id) ?? 0 : 0
      bySection[r.section].push({ row: r, balance, comp })
    }
  }

  function toAccountWithBalance(rw: RowWithComp, section: string): AccountWithBalance {
    return {
      account: {
        id: rw.row.account_id ?? '',
        code: rw.row.code ?? '',
        name: rw.row.name ?? '',
        account_type: accountTypeForSection(section),
        subgroup_id: null,
        subgroup_code: rw.row.subgroup_code,
        subgroup_name: rw.row.subgroup_name ?? undefined,
      },
      balance: rw.balance,
    }
  }

  function buildLines(section: string, order: Record<string, number>, isNCA = false): BSLine[] {
    const grouped = new Map<string, RowWithComp[]>()
    const ungrouped: BSLine[] = []

    for (const rw of bySection[section]) {
      const sg = rw.row.subgroup_code
      if (sg) {
        if (!grouped.has(sg)) grouped.set(sg, [])
        grouped.get(sg)!.push(rw)
      } else {
        ungrouped.push({
          label: rw.row.name ?? '',
          amount: rw.balance,
          comparative: wantComp ? rw.comp : undefined,
          subgroupCode: '',
          accounts: [toAccountWithBalance(rw, section)],
        })
      }
    }

    const lines: BSLine[] = []

    if (isNCA) {
      const faGroup = grouped.get('FA') ?? []
      const acdGroup = grouped.get('ACD') ?? []
      grouped.delete('FA')
      grouped.delete('ACD')

      if (faGroup.length > 0 || acdGroup.length > 0) {
        const faTotal = faGroup.reduce((s, p) => s + p.balance, 0)
        const acdTotal = acdGroup.reduce((s, p) => s + p.balance, 0)
        const faComp = wantComp ? faGroup.reduce((s, p) => s + p.comp, 0) : undefined
        const acdComp = wantComp ? acdGroup.reduce((s, p) => s + p.comp, 0) : undefined

        if (faGroup.length > 0) {
          lines.push({
            label: SUBGROUP_LABELS.FA?.label ?? 'Property, Plant & Equipment',
            amount: faTotal,
            comparative: faComp,
            noteRef: SUBGROUP_LABELS.FA?.noteRef,
            subgroupCode: 'FA',
            accounts: faGroup.map(p => toAccountWithBalance(p, section)),
          })
        }
        if (acdGroup.length > 0) {
          lines.push({
            label: 'Less: Accumulated Depreciation',
            amount: acdTotal,
            comparative: acdComp,
            isNegative: true,
            subgroupCode: 'ACD',
            accounts: acdGroup.map(p => toAccountWithBalance(p, section)),
          })
        }
        lines.push({
          label: 'Net Book Value',
          amount: faTotal + acdTotal,
          comparative: wantComp ? (faComp ?? 0) + (acdComp ?? 0) : undefined,
          isSubtotal: true,
          subgroupCode: 'FA',
          accounts: [],
        })
      }
    }

    const sortedGroups = Array.from(grouped.entries()).sort(
      ([a], [b]) => (order[a] ?? 99) - (order[b] ?? 99)
    )
    for (const [sg, grp] of sortedGroups) {
      const info = SUBGROUP_LABELS[sg]
      lines.push({
        label: info?.label ?? grp[0].row.subgroup_name ?? sg,
        amount: grp.reduce((s, p) => s + p.balance, 0),
        comparative: wantComp ? grp.reduce((s, p) => s + p.comp, 0) : undefined,
        noteRef: info?.noteRef,
        subgroupCode: sg,
        accounts: grp.map(p => toAccountWithBalance(p, section)),
      })
    }

    lines.push(...ungrouped)
    return lines
  }

  function buildEquityLines(): BSLine[] {
    const lines: BSLine[] = []
    const rePairs: RowWithComp[] = []
    const otherBySubgroup = new Map<string, RowWithComp[]>()
    const ungrouped: RowWithComp[] = []

    for (const rw of bySection.equity) {
      const sg = rw.row.subgroup_code
      if (sg === 'OWF') {
        lines.push({
          label: rw.row.name ?? '',
          amount: rw.balance,
          comparative: wantComp ? rw.comp : undefined,
          subgroupCode: 'OWF',
          accounts: [toAccountWithBalance(rw, 'equity')],
        })
      } else if (sg === 'RE' || rw.row.code === '3003') {
        rePairs.push(rw)
      } else if (sg) {
        if (!otherBySubgroup.has(sg)) otherBySubgroup.set(sg, [])
        otherBySubgroup.get(sg)!.push(rw)
      } else {
        ungrouped.push(rw)
      }
    }

    for (const [sg, grp] of otherBySubgroup) {
      const info = SUBGROUP_LABELS[sg]
      lines.push({
        label: info?.label ?? grp[0].row.subgroup_name ?? sg,
        amount: grp.reduce((s, p) => s + p.balance, 0),
        comparative: wantComp ? grp.reduce((s, p) => s + p.comp, 0) : undefined,
        noteRef: info?.noteRef,
        subgroupCode: sg,
        accounts: grp.map(p => toAccountWithBalance(p, 'equity')),
      })
    }

    for (const rw of ungrouped) {
      lines.push({
        label: rw.row.name ?? '',
        amount: rw.balance,
        comparative: wantComp ? rw.comp : undefined,
        subgroupCode: '',
        accounts: [toAccountWithBalance(rw, 'equity')],
      })
    }

    if (rePairs.length > 0) {
      lines.push({
        label: SUBGROUP_LABELS.RE?.label ?? 'Retained Earnings',
        amount: rePairs.reduce((s, p) => s + p.balance, 0),
        comparative: wantComp ? rePairs.reduce((s, p) => s + p.comp, 0) : undefined,
        subgroupCode: 'RE',
        accounts: rePairs.map(p => toAccountWithBalance(p, 'equity')),
      })
    }

    const cpp = synthetic.get('current_period_profit') ?? 0
    const compCpp = wantComp ? compSynthetic.get('current_period_profit') ?? 0 : undefined
    lines.push({
      label: 'Current Period Profit',
      amount: cpp,
      comparative: compCpp,
      subgroupCode: 'CPP',
      accounts: [],
    })

    return lines
  }

  const nonCurrentAssets = buildLines('non_current_assets', NON_CURRENT_ASSET_ORDER, true)
  const currentAssets = buildLines('current_assets', CURRENT_ASSET_ORDER)
  const nonCurrentLiabilities = buildLines('non_current_liabilities', {})
  const currentLiabilities = buildLines('current_liabilities', CURRENT_LIAB_ORDER)
  const equity = buildEquityLines()

  const totalNCA = bySection.non_current_assets.reduce((s, p) => s + p.balance, 0)
  const totalCA = bySection.current_assets.reduce((s, p) => s + p.balance, 0)
  const totalNCL = bySection.non_current_liabilities.reduce((s, p) => s + p.balance, 0)
  const totalCL = bySection.current_liabilities.reduce((s, p) => s + p.balance, 0)
  const totalAssets = synthetic.get('total_assets') ?? totalNCA + totalCA
  const totalLiabilities = synthetic.get('total_liabilities') ?? totalNCL + totalCL
  const totalEquity = synthetic.get('total_equity')
    ?? bySection.equity.reduce((s, p) => s + p.balance, 0) + (synthetic.get('current_period_profit') ?? 0)

  const comparativeTotalNonCurrentAssets = wantComp ? compSectionTotal.get('non_current_assets') ?? 0 : undefined
  const comparativeTotalCurrentAssets = wantComp ? compSectionTotal.get('current_assets') ?? 0 : undefined
  const comparativeTotalNonCurrentLiabilities = wantComp ? compSectionTotal.get('non_current_liabilities') ?? 0 : undefined
  const comparativeTotalCurrentLiabilities = wantComp ? compSectionTotal.get('current_liabilities') ?? 0 : undefined
  const comparativeTotalAssets = wantComp ? compSynthetic.get('total_assets') ?? 0 : undefined
  const comparativeTotalLiabilities = wantComp ? compSynthetic.get('total_liabilities') ?? 0 : undefined
  const comparativeTotalEquity = wantComp ? compSynthetic.get('total_equity') ?? 0 : undefined

  const isBalanced = Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01

  return {
    asOfDate,
    comparativeDate: comparativeAsOfDate,
    nonCurrentAssets,
    currentAssets,
    equity,
    nonCurrentLiabilities,
    currentLiabilities,
    totalNonCurrentAssets: totalNCA,
    totalCurrentAssets: totalCA,
    totalAssets,
    totalNonCurrentLiabilities: totalNCL,
    totalCurrentLiabilities: totalCL,
    totalLiabilities,
    totalEquity,
    comparativeTotalNonCurrentAssets,
    comparativeTotalCurrentAssets,
    comparativeTotalAssets,
    comparativeTotalNonCurrentLiabilities,
    comparativeTotalCurrentLiabilities,
    comparativeTotalLiabilities,
    comparativeTotalEquity,
    isBalanced,
  }
}

// ─── Income Statement ─────────────────────────────────────────────────────────

type IncomeStatementRpcRow = {
  section: string
  section_order: number
  account_id: string | null
  code: string | null
  name: string | null
  group_code: string | null
  group_name: string | null
  subgroup_code: string | null
  subgroup_name: string | null
  amount: number | string | null
  is_subtotal: boolean
}

export async function calculateIncomeStatement(
  tenantId: string,
  fromDate: string,
  toDate: string,
  locationId?: string | null,
  comparativeFromDate?: string,
  comparativeToDate?: string,
): Promise<IncomeStatementData> {
  async function fetchRpc(from: string, to: string): Promise<IncomeStatementRpcRow[]> {
    const { data, error } = await supabase.rpc('get_income_statement', {
      p_tenant_id: tenantId,
      p_location_id: locationId ?? undefined,
      p_from_date: from,
      p_to_date: to,
      p_include_zero: false,
    })
    if (error) throw new Error(error.message)
    return (data ?? []) as IncomeStatementRpcRow[]
  }

  const wantComp = !!(comparativeFromDate && comparativeToDate)
  const [rows, compRows] = await Promise.all([
    fetchRpc(fromDate, toDate),
    wantComp
      ? fetchRpc(comparativeFromDate as string, comparativeToDate as string)
      : Promise.resolve<IncomeStatementRpcRow[]>([]),
  ])

  const num = (v: number | string | null | undefined): number => Number(v ?? 0)

  const compByAccount = new Map<string, number>()
  const compSectionTotal = new Map<string, number>()
  const compSubtotal = new Map<string, number>()
  for (const r of compRows) {
    const amt = num(r.amount)
    if (r.is_subtotal) {
      compSubtotal.set(r.section, amt)
    } else {
      if (r.account_id) compByAccount.set(r.account_id, (compByAccount.get(r.account_id) ?? 0) + amt)
      compSectionTotal.set(r.section, (compSectionTotal.get(r.section) ?? 0) + amt)
    }
  }

  function rowToLine(r: IncomeStatementRpcRow): StatementLine {
    return {
      id: r.account_id ?? '',
      code: r.code ?? '',
      name: r.name ?? '',
      balance: num(r.amount),
      comparative: wantComp ? (r.account_id ? compByAccount.get(r.account_id) ?? 0 : 0) : undefined,
      subgroup_code: r.subgroup_code ?? null,
      subgroup_name: r.subgroup_name ?? null,
    }
  }

  const buckets: Record<string, StatementLine[]> = {
    revenue: [],
    cost_of_sales: [],
    other_income: [],
    operating_expenses: [],
    finance_costs: [],
    taxation: [],
  }
  let grossProfit = 0
  let operatingProfit = 0
  let profitBeforeTax = 0

  for (const r of rows) {
    if (r.is_subtotal) {
      const amt = num(r.amount)
      if (r.section === 'gross_profit') grossProfit = amt
      else if (r.section === 'operating_profit') operatingProfit = amt
      else if (r.section === 'profit_before_tax') profitBeforeTax = amt
    } else if (buckets[r.section]) {
      buckets[r.section].push(rowToLine(r))
    }
  }

  const sectionTotal = (s: string): number => buckets[s].reduce((acc, l) => acc + l.balance, 0)

  const revenueTotal = sectionTotal('revenue')
  const otherIncomeTotal = sectionTotal('other_income')
  const cogsTotal = sectionTotal('cost_of_sales')
  const opExpTotal = sectionTotal('operating_expenses')
  const financeCostsTotal = sectionTotal('finance_costs')
  const taxationTotal = sectionTotal('taxation')
  const profitAfterTax = profitBeforeTax - taxationTotal

  const comparativeGrossProfit = wantComp ? compSubtotal.get('gross_profit') ?? 0 : undefined
  const comparativeOperatingProfit = wantComp ? compSubtotal.get('operating_profit') ?? 0 : undefined
  const comparativeProfitBeforeTax = wantComp ? compSubtotal.get('profit_before_tax') ?? 0 : undefined
  const comparativeIncomeTax = wantComp ? compSectionTotal.get('taxation') ?? 0 : undefined
  const comparativeProfitAfterTax = wantComp
    ? (comparativeProfitBeforeTax ?? 0) - (comparativeIncomeTax ?? 0)
    : undefined

  return {
    fromDate,
    toDate,
    comparativeFromDate,
    comparativeToDate,
    revenue: {
      title: 'Revenue',
      lines: buckets.revenue,
      total: revenueTotal,
      comparativeTotal: wantComp ? compSectionTotal.get('revenue') ?? 0 : undefined,
    },
    otherIncome: {
      title: 'Other Income',
      lines: buckets.other_income,
      total: otherIncomeTotal,
      comparativeTotal: wantComp ? compSectionTotal.get('other_income') ?? 0 : undefined,
    },
    costOfSales: {
      title: 'Cost of Sales',
      lines: buckets.cost_of_sales,
      total: cogsTotal,
      comparativeTotal: wantComp ? compSectionTotal.get('cost_of_sales') ?? 0 : undefined,
    },
    grossProfit,
    comparativeGrossProfit,
    operatingExpenses: {
      title: 'Operating Expenses',
      lines: buckets.operating_expenses,
      total: opExpTotal,
      comparativeTotal: wantComp ? compSectionTotal.get('operating_expenses') ?? 0 : undefined,
    },
    operatingProfit,
    comparativeOperatingProfit,
    financeIncome: {
      title: 'Finance Income',
      lines: [],
      total: 0,
      comparativeTotal: wantComp ? 0 : undefined,
    },
    financeCosts: {
      title: 'Finance Costs',
      lines: buckets.finance_costs,
      total: financeCostsTotal,
      comparativeTotal: wantComp ? compSectionTotal.get('finance_costs') ?? 0 : undefined,
    },
    profitBeforeTax,
    comparativeProfitBeforeTax,
    incomeTax: taxationTotal,
    comparativeIncomeTax,
    profitAfterTax,
    comparativeProfitAfterTax,
  }
}

// ─── Cash Flow (Indirect Method) ──────────────────────────────────────────────

type CashFlowRpcRow = {
  section: string
  section_order: number
  line_key: string
  name: string | null
  amount: number | string | null
  is_subtotal: boolean
}

export async function calculateCashFlow(
  tenantId: string,
  fromDate: string,
  toDate: string,
  locationId?: string | null,
  comparativeFromDate?: string,
  comparativeToDate?: string,
): Promise<CashFlowData> {
  async function fetchRpc(from: string, to: string): Promise<CashFlowRpcRow[]> {
    const { data, error } = await supabase.rpc('get_cash_flow_statement', {
      p_tenant_id: tenantId,
      p_location_id: locationId ?? undefined,
      p_from_date: from,
      p_to_date: to,
    })
    if (error) throw new Error(error.message)
    return (data ?? []) as CashFlowRpcRow[]
  }

  const wantComp = !!(comparativeFromDate && comparativeToDate)
  const [rows] = await Promise.all([
    fetchRpc(fromDate, toDate),
    wantComp
      ? fetchRpc(comparativeFromDate as string, comparativeToDate as string)
      : Promise.resolve<CashFlowRpcRow[]>([]),
  ])

  const m = new Map<string, number>()
  for (const r of rows) {
    m.set(r.line_key, Number(r.amount ?? 0))
  }
  const v = (key: string): number => m.get(key) ?? 0

  return {
    fromDate,
    toDate,
    netProfit: v('profit_before_tax'),
    depreciation: v('depreciation'),
    amortisation: 0,
    changeReceivables: v('change_receivables'),
    changeInventory: v('change_inventory'),
    changePayables: v('change_payables'),
    changePrepayments: v('change_prepayments'),
    changeAccruedLiabilities: v('change_other_cl'),
    netOperating: v('net_operating'),
    purchaseFixedAssets: v('purchase_fixed_assets'),
    proceedsAssetDisposal: v('proceeds_disposals'),
    netInvesting: v('net_investing'),
    loanMovement: v('loan_movement'),
    ownerDrawings: v('owner_drawings'),
    dividendsPaid: 0,
    netFinancing: v('net_financing'),
    netCashMovement: v('net_change_in_cash'),
    openingCash: v('opening_cash'),
    closingCash: v('closing_cash_actual'),
  }
}

// ─── Statement of Changes in Equity ──────────────────────────────────────────

type ChangesInEquityRpcRow = {
  component_key: string
  component_name: string
  component_order: number
  opening_balance: number | string | null
  movement: number | string | null
  closing_balance: number | string | null
  is_total: boolean
}

export async function calculateChangesInEquity(
  tenantId: string,
  fromDate: string,
  toDate: string,
  locationId?: string | null,
): Promise<ChangesInEquityData> {
  const { data, error } = await supabase.rpc('get_changes_in_equity', {
    p_tenant_id: tenantId,
    p_location_id: locationId ?? undefined,
    p_from_date: fromDate,
    p_to_date: toDate,
  })
  if (error) throw new Error(error.message)

  const rows = ((data ?? []) as ChangesInEquityRpcRow[])
    .slice()
    .sort((a, b) => a.component_order - b.component_order)

  const components: EquityComponent[] = rows.map(r => ({
    key: r.component_key,
    name: r.component_name,
    opening: Number(r.opening_balance ?? 0),
    movement: Number(r.movement ?? 0),
    closing: Number(r.closing_balance ?? 0),
    isTotal: r.is_total,
  }))

  return { fromDate, toDate, components }
}
